import type { PageProps } from '../app';
import { Icon } from '../ui/components/Icon';
import { Card } from '../ui/components/Section';
import { href } from '../router';
import { Breadcrumbs } from '../ui/components/Breadcrumbs';

const ext = (href: string, label: string) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {label} <Icon name="external" size={12} />
  </a>
);

/** Thanks to the services, data and software this explorer relies on. */
export function CreditsPage(_props: PageProps) {
  return (
    <main class="content page-narrow">
      <Breadcrumbs items={[{ label: 'Home', href: href('/') }, { label: 'About' }]} />
      <div class="page-head">
        <div>
          <h1>About &amp; credits</h1>
          <p class="lede">Merkle Lens runs entirely in your browser, but it stands on the work of others. Thank you.</p>
        </div>
      </div>
      <div class="stack">
        <Card title={<><Icon name="link" /> Services we call</>}>
          <dl class="kv">
            <dt>{ext('https://whatsmychaincert.com', "What's My Chain Cert?")}</dt>
            <dd>
              By {ext('https://sslmate.com', 'SSLMate')}. Its <code>generate</code> API builds the correct issuer chain for a certificate or precertificate and can retrieve a live server's certificate — and it sends CORS headers, which is what lets the web version do this without a proxy. Used by "Complete the chain" and "Fetch from host" in the web version; the desktop app does both locally (its own TLS handshake, AIA and the system trust store).
            </dd>
            <dt>{ext('https://crt.sh', 'crt.sh')}</dt>
            <dd>By {ext('https://sectigo.com', 'Sectigo')}. Its <code>gen-add-chain</code> endpoint is the fallback chain builder (reached through the proxy, as it sends no CORS headers).</dd>
            <dt>The CT logs</dt>
            <dd>Every tree head, entry, tile and proof comes straight from the log operators' servers — Google, Cloudflare, DigiCert, Sectigo, Let's Encrypt, TrustAsia, Geomys, IPng Networks, Microsec and others. Operators that send CORS headers make the browser-only experience possible.</dd>
            <dt>Log lists</dt>
            <dd>{ext('https://www.gstatic.com/ct/log_list/v3/all_logs_list.json', "Google's Chrome log list")} and {ext('https://valid.apple.com/ct/log_list/current_log_list.json', "Apple's log list")}, both CORS-enabled; Firefox derives its list from Chrome's.</dd>
          </dl>
        </Card>
        <Card title={<><Icon name="file" /> Specifications</>}>
          <ul class="plain stack" style="gap:0.3rem">
            <li>{ext('https://www.rfc-editor.org/rfc/rfc6962', 'RFC 6962 — Certificate Transparency')} and {ext('https://www.rfc-editor.org/rfc/rfc9162', 'RFC 9162')} (proof algorithms)</li>
            <li>{ext('https://c2sp.org/static-ct-api', 'C2SP static-ct-api')}, {ext('https://c2sp.org/tlog-tiles', 'tlog-tiles')}, {ext('https://c2sp.org/tlog-checkpoint', 'tlog-checkpoint')} and {ext('https://c2sp.org/signed-note', 'signed-note')} — the tiled-log formats pioneered by {ext('https://sunlight.dev', 'Sunlight')}</li>
          </ul>
        </Card>
        <Card title={<><Icon name="shield" /> Test data</>}>
          <p class="small">Merkle proof test vectors are from {ext('https://github.com/transparency-dev/merkle', 'transparency-dev/merkle')} (Apache-2.0). Live fixtures were captured from Google Argon and IPng Gouda; the example certificate is example.com's.</p>
        </Card>
        <Card title={<><Icon name="settings" /> Software</>}>
          <p class="small">
            {ext('https://preactjs.com', 'Preact')}, {ext('https://vite.dev', 'Vite')}, {ext('https://vitest.dev', 'Vitest')}, {ext('https://github.com/PeculiarVentures/x509', '@peculiar/x509')} and {ext('https://github.com/PeculiarVentures/ASN1.js', 'asn1js')} for X.509 parsing, the browser's WebCrypto for every hash and signature, {ext('https://github.com/webview/webview', 'webview/webview')} and {ext('https://curl.se/libcurl/', 'libcurl')} for the desktop app and {ext('https://workers.cloudflare.com', 'Cloudflare Workers')} for the optional proxy.
          </p>
        </Card>
      </div>
    </main>
  );
}
